import { t } from '@/shared/lib/i18n'
import { showErrorToast } from '@/shared/lib/toast'

const OBJECT_URL_REVOKE_DELAY_MS = 60_000

const FILENAME_PATTERN = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i

const filenameFromDisposition = (disposition: string | null, fallback: string): string => {
  const match = disposition ? FILENAME_PATTERN.exec(disposition) : null
  if (!match?.[1]) return fallback
  try {
    return decodeURIComponent(match[1])
  } catch {
    return match[1]
  }
}

const saveBlob = (blob: Blob, filename: string): void => {
  const objectUrl = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = objectUrl
  anchor.download = filename
  anchor.rel = 'noopener'
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), OBJECT_URL_REVOKE_DELAY_MS)
}

/**
 * Download a report export (PDF/CSV) without navigating the tab (REBUILD-584).
 * Assigning `window.location.href` replaces the app page whenever the server answers with
 * anything other than an attachment (redirect, error page, inline PDF); fetching keeps the
 * user on the report and surfaces a toast instead.
 */
export const downloadExport = async (url: string, fallbackFilename: string): Promise<void> => {
  try {
    const response = await fetch(url, { credentials: 'same-origin' })
    const contentType = response.headers.get('Content-Type') ?? ''
    if (!response.ok || contentType.toLowerCase().startsWith('text/html')) {
      throw new Error(`Export failed with status ${response.status}`)
    }
    const blob = await response.blob()
    saveBlob(
      blob,
      filenameFromDisposition(response.headers.get('Content-Disposition'), fallbackFilename),
    )
  } catch {
    showErrorToast(t('common.exportFailed'))
  }
}
